import { useEffect, useMemo, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	IconButton,
	MenuItem,
	Pagination,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AttrConfigDrawer from "./AttrConfigDrawer";
import { getFilterOptions, getOptionSearch, getSalesFilterOptions, getSizeContributionReport } from "./api";

/** Historical ACH scope: EBO / MBO / LFS only; optional store / state / region (dependent on channel). */
function buildSalesFiltersPayload(f) {
	if (!f) return null;
	const out = {};
	if (f.channel?.length) out.channel = f.channel.join(",");
	if (f.store?.length) out.store = f.store.join(",");
	if (f.state?.length) out.state = f.state.join(",");
	if (f.region?.length) out.region = f.region.join(",");
	return Object.keys(out).length ? out : null;
}

const SALES_CHANNEL_OPTIONS = ["EBO", "MBO", "LFS"];

const emptySalesFilterSelection = {
	channel: [],
	store: [],
	state: [],
	region: [],
};

// ─── Image Lightbox ──────────────────────────────────────────────────────────
const ImageLightbox = ({ src, alt, open, onClose }) => {
	const [zoom, setZoom] = useState(1);
	const STEP = 0.25;
	const MIN = 0.5;
	const MAX = 4;

	useEffect(() => {
		if (open) setZoom(1);
	}, [open, src]);

	if (!src) return null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: "transparent", boxShadow: "none", overflow: "visible" } }}>
			<Box
				sx={{
					position: "relative",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					background: "rgba(15,15,15,0.92)",
					borderRadius: 2,
					p: 1,
					minWidth: 320,
					minHeight: 200,
				}}>
				{/* Controls */}
				<Stack
					direction="row"
					alignItems="center"
					gap={0.5}
					sx={{
						position: "absolute",
						top: 8,
						right: 8,
						zIndex: 10,
						background: "rgba(0,0,0,0.55)",
						borderRadius: 2,
						px: 0.5,
						py: 0.3,
					}}>
					<Tooltip title="Zoom out"><span>
						<IconButton size="small" onClick={() => setZoom((z) => Math.max(MIN, +(z - STEP).toFixed(2)))} disabled={zoom <= MIN} sx={{ color: "#fff" }}>
							<ZoomOutIcon fontSize="small" />
						</IconButton>
					</span></Tooltip>
					<Typography sx={{ fontSize: "0.72rem", color: "#fff", minWidth: 36, textAlign: "center" }}>
						{Math.round(zoom * 100)}%
					</Typography>
					<Tooltip title="Zoom in"><span>
						<IconButton size="small" onClick={() => setZoom((z) => Math.min(MAX, +(z + STEP).toFixed(2)))} disabled={zoom >= MAX} sx={{ color: "#fff" }}>
							<ZoomInIcon fontSize="small" />
						</IconButton>
					</span></Tooltip>
					<Tooltip title="Reset zoom">
						<IconButton size="small" onClick={() => setZoom(1)} sx={{ color: "#fff" }}>
							<ZoomOutMapIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} sx={{ color: "#fff", ml: 0.5 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>

				{/* Image */}
				<Box sx={{ overflow: "auto", maxWidth: "90vw", maxHeight: "85vh", mt: 4, borderRadius: 1 }}>
					<Box
						component="img"
						src={src}
						alt={alt}
						sx={{
							display: "block",
							transform: `scale(${zoom})`,
							transformOrigin: "top left",
							width: `${Math.round(zoom * 400)}px`,
							transition: "transform 0.15s ease",
						}}
					/>
				</Box>
			</Box>
		</Dialog>
	);
};

// Thumbnail that opens the lightbox on click
const ZoomableImage = ({ src, alt }) => {
	const [open, setOpen] = useState(false);
	if (!src) {
		return (
			<Box sx={{ width: 56, height: 72, display: "grid", placeItems: "center", bgcolor: "#f9fafb", color: "#9ca3af", fontSize: "0.6rem", borderRadius: 1, border: "1px solid #d1d5db" }}>
				No img
			</Box>
		);
	}
	return (
		<>
			<Box sx={{ position: "relative", display: "inline-block", cursor: "zoom-in" }} onClick={() => setOpen(true)}>
				<Box component="img" src={src} alt={alt} sx={{ width: 56, height: 72, objectFit: "cover", borderRadius: 1, border: "1px solid #d1d5db", display: "block" }} />
				<Box sx={{
					position: "absolute", inset: 0, borderRadius: 1,
					display: "flex", alignItems: "center", justifyContent: "center",
					opacity: 0, transition: "opacity 0.15s",
					bgcolor: "rgba(0,0,0,0.35)",
					"&:hover": { opacity: 1 },
				}}>
					<ZoomInIcon sx={{ color: "#fff", fontSize: "1.2rem" }} />
				</Box>
			</Box>
			<ImageLightbox src={src} alt={alt} open={open} onClose={() => setOpen(false)} />
		</>
	);
};

const getDefaultSeason = () => {
	const now = new Date();
	const month = now.getMonth() + 1;
	const year = now.getFullYear();
	if (month >= 2 && month <= 7) return `SS-${String(year).slice(-2)}`;
	return `AW-${String(month >= 8 ? year + 1 : year).slice(-2)}`;
};

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL", "5XL"];

function sortSizes(cols) {
	return [...cols].sort((a, b) => {
		const ia = SIZE_ORDER.indexOf(a);
		const ib = SIZE_ORDER.indexOf(b);
		if (ia !== -1 && ib !== -1) return ia - ib;
		if (ia !== -1) return -1;
		if (ib !== -1) return 1;
		const na = parseInt(a), nb = parseInt(b);
		if (!isNaN(na) && !isNaN(nb)) return na - nb;
		return a.localeCompare(b);
	});
}

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	whiteSpace: "nowrap",
	backgroundColor: "#f3f4f6",
	color: "#374151",
	borderColor: "#d1d5db",
	py: 0.8,
	px: 1,
};

const bodyCellSx = {
	fontSize: "0.68rem",
	color: "#111827",
	borderColor: "#e5e7eb",
	py: 0.3,
	px: 1,
	verticalAlign: "middle",
};

const pctCellSx = {
	...bodyCellSx,
	color: "#6b7280",
	borderBottom: "none",
};

const splitCellSx = {
	...bodyCellSx,
	color: "#854d0e",
	backgroundColor: "#fffbeb",
	fontWeight: 600,
	borderBottom: "1px solid #e5e7eb",
};

/** MUI Outlined: same label position for empty + filled (avoids “Current Season” vs “Brand” mismatch). */
const filterOutlinedLabelProps = { shrink: true };
const relookSelectFieldSx = { minWidth: 0, width: "100%" };
const salesAutocompleteFieldSx = { minWidth: 180, maxWidth: 240, flex: "0 0 auto" };

const measureLabelSx = {
	...bodyCellSx,
	fontSize: "0.6rem",
	fontWeight: 600,
	color: "#4b5563",
	backgroundColor: "#f9fafb",
	maxWidth: 108,
	whiteSpace: "normal",
	lineHeight: 1.2,
};


const PREV_SEASON_COLORS = ["#1d4ed8", "#059669", "#b45309", "#7c3aed", "#be123c"];

/** Fixed buy pack for split row — same as Excel: `=ROUNDUP(share*8,0)` where share = contribution as decimal. */
const SPLIT_BUY_QTY = 8;

function roundUpSplitFromPct(contributionPct, totalSold) {
	if (totalSold <= 0 || contributionPct == null) return 0;
	const share = Number(contributionPct) / 100;
	return Math.ceil(share * SPLIT_BUY_QTY);
}

// Build display PKEY: BRAND-CATEGORY-STYLE-COLOR-PALETTE
function buildDisplayPkey(row) {
	const parts = (row.option_code || "").split("-");
	if (parts.length >= 2 && row.style) {
		return [parts[0], parts[1], row.style, ...parts.slice(2)].join("-");
	}
	return row.option_code || "";
}

// ─── Report Table ───────────────────────────────────────────────────────────
// Left: current season option (image, PKEY, category, sub cat)
// Right: for each prev season, aggregated size ratio columns
const ReportTable = ({ currentOptions = [], prevSeasons = [], currentSeason }) => {
	// Collect all unique sizes across all prev seasons across all options
	const sizesByPrevSeason = useMemo(() => {
		const map = {};
		prevSeasons.forEach((season) => {
			const seen = new Set();
			currentOptions.forEach((opt) => {
				const sizes = opt.prev_season_data?.[season]?.sizes || [];
				sizes.forEach((s) => seen.add(s.size));
			});
			map[season] = sortSizes(Array.from(seen));
		});
		return map;
	}, [currentOptions, prevSeasons]);

	if (!currentOptions.length) {
		return (
			<Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1, mb: 2 }}>
				<Typography variant="body2" color="text.secondary">No options in the current season result.</Typography>
			</Box>
		);
	}

	const totalPrevCols = prevSeasons.reduce((acc, s) => acc + (sizesByPrevSeason[s]?.length || 0) + 1, 0); // +1 for Total per season

	return (
		<TableContainer sx={{ border: "1px solid #d1d5db", borderRadius: 1 }}>
			<Table size="small" stickyHeader>
				<TableHead>
					{/* Row 1: section labels */}
					<TableRow>
						{/* Current season static cols */}
						<TableCell sx={headerCellSx} rowSpan={2}>#</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>URL</TableCell>
						<TableCell sx={{ ...headerCellSx, maxWidth: 200 }} rowSpan={2}>PKEY</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Category</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Sub Cat</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Color</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Palette</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Fashionability</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Occasion</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Sleeve</TableCell>
						<TableCell sx={headerCellSx} rowSpan={2}>Sub Brand</TableCell>
						<TableCell sx={{ ...headerCellSx, maxWidth: 114, verticalAlign: "top" }} rowSpan={2}>
							<Typography sx={{ fontSize: "0.62rem", fontWeight: 800, lineHeight: 1.25 }}>Historical contribution</Typography>
							<Typography sx={{ fontSize: "0.58rem", color: "#6b7280", mt: 0.75, fontWeight: 700 }}>Split of Qty Sold</Typography>
							<Typography sx={{ fontSize: "0.55rem", color: "#92400e", mt: 0.25 }}>Ratio Split</Typography>
						</TableCell>
						{/* Prev season group headers */}
						{prevSeasons.map((season, si) => {
							const cols = sizesByPrevSeason[season] || [];
							const color = PREV_SEASON_COLORS[si % PREV_SEASON_COLORS.length];
							return (
								<TableCell
									key={`grp-${season}`}
									colSpan={cols.length + 1}
									sx={{ ...headerCellSx, borderLeft: "2px solid #d1d5db", textAlign: "center", color, backgroundColor: "#f0f4ff" }}>
									{season} — Suggested Size Ratio
								</TableCell>
							);
						})}
					</TableRow>
					{/* Row 2: individual size columns + Total per season */}
					<TableRow>
						{prevSeasons.map((season, si) => {
							const cols = sizesByPrevSeason[season] || [];
							const color = PREV_SEASON_COLORS[si % PREV_SEASON_COLORS.length];
							return [
								...cols.map((size, ci) => (
									<TableCell
										key={`sz-${season}-${size}`}
										sx={{
											...headerCellSx,
											textAlign: "center",
											color,
											backgroundColor: "#f0f4ff",
											borderLeft: ci === 0 ? "2px solid #d1d5db" : undefined,
										}}>
										{size}
									</TableCell>
								)),
								<TableCell
									key={`total-${season}`}
									sx={{ ...headerCellSx, textAlign: "center", color, backgroundColor: "#f0f4ff", fontStyle: "italic" }}>
									Total
								</TableCell>,
							];
						})}
					</TableRow>
				</TableHead>

				<TableBody>
					{currentOptions.map((opt, idx) => {
						const rowId = opt.report_row_key || `${opt.option_code}-${idx}`;
						const displayPkey = buildDisplayPkey(opt);
						return [
							/* Row 1 — Historical sold qty */
							<TableRow key={`qty-${rowId}`}>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{idx + 1}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>
									<ZoomableImage src={opt.image_url} alt={displayPkey} />
								</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none", maxWidth: 200 }} rowSpan={3}>
									<Tooltip
										title={
											<Box>
												<div><b>PKEY:</b> {displayPkey}</div>
												{opt.attrs_used?.length ? (
													<div style={{ marginTop: 4 }}><b>Matched on:</b> {opt.attrs_used.join(", ")}</div>
												) : null}
												{opt.attrs_used?.map((a) => (
													<div key={a} style={{ fontSize: "0.7rem" }}>• {a.replace(/_/g, " ")}: {opt[a] || "—"}</div>
												))}
											</Box>
										}
										placement="top"
										arrow>
										<Typography sx={{ fontSize: "0.68rem", fontWeight: 600, lineHeight: 1.4, wordBreak: "break-word", whiteSpace: "normal", cursor: "help" }}>
											{displayPkey}
										</Typography>
									</Tooltip>
								</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.category || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.sub_category || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.color || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.palette_type || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.fashionability || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.occasion || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.sleeve || "-"}</TableCell>
								<TableCell sx={{ ...bodyCellSx, borderBottom: "none" }} rowSpan={3}>{opt.sub_brand || "-"}</TableCell>
								<TableCell sx={{ ...measureLabelSx, borderBottom: "none" }}>Sold Qty (Historical)</TableCell>
								{/* Prev season qty cells */}
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const sizeMap = Object.fromEntries(sizes.map((s) => [s.size, s.sold_qty]));
									const total = opt.prev_season_data?.[season]?.total_sold ?? 0;
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`qty-${rowId}-${season}-${size}`}
												sx={{ ...bodyCellSx, borderBottom: "none", textAlign: "center", borderLeft: ci === 0 ? "2px solid #d1d5db" : undefined }}>
												{sizeMap[size] ?? 0}
											</TableCell>
										)),
										<TableCell
											key={`total-qty-${rowId}-${season}`}
											sx={{ ...bodyCellSx, borderBottom: "none", textAlign: "center", fontWeight: 600 }}>
											{total.toLocaleString()}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* Row 2 — Historical % */
							<TableRow key={`pct-${rowId}`}>
								<TableCell sx={{ ...measureLabelSx, color: "#6b7280", borderBottom: "none" }}>Share % (Historical)</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const pctMap = Object.fromEntries(sizes.map((s) => [s.size, s.contribution_pct]));
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`pct-${rowId}-${season}-${size}`}
												sx={{ ...pctCellSx, textAlign: "center", borderLeft: ci === 0 ? "2px solid #d1d5db" : undefined }}>
												{pctMap[size] != null ? `${pctMap[size]}%` : "0%"}
											</TableCell>
										)),
										<TableCell
											key={`total-pct-${rowId}-${season}`}
											sx={{ ...pctCellSx, textAlign: "center", fontStyle: "italic", fontSize: "0.62rem" }}>
											{totalSold > 0 ? "100%" : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,

							/* Row 3 — Split of Qty Sold: ROUNDUP(share×8) per Excel */
							<TableRow key={`split-${rowId}`}>
								<TableCell sx={measureLabelSx}>Ratio Split (Historical)</TableCell>
								{prevSeasons.map((season, si) => {
									const cols = sizesByPrevSeason[season] || [];
									const sizes = opt.prev_season_data?.[season]?.sizes || [];
									const totalSold = opt.prev_season_data?.[season]?.total_sold ?? 0;
									const pctMap = Object.fromEntries(sizes.map((s) => [s.size, s.contribution_pct]));
									const splitMap = Object.fromEntries(
										cols.map((size) => {
											const p = pctMap[size];
											return [size, roundUpSplitFromPct(p, totalSold)];
										}),
									);
									const splitSum = cols.reduce((acc, size) => acc + (splitMap[size] || 0), 0);
									return [
										...cols.map((size, ci) => (
											<TableCell
												key={`split-${rowId}-${season}-${size}`}
												sx={{ ...splitCellSx, textAlign: "center", borderLeft: ci === 0 ? "2px solid #d1d5db" : undefined }}>
												{totalSold > 0 ? splitMap[size] : "—"}
											</TableCell>
										)),
										<TableCell
											key={`total-split-${rowId}-${season}`}
											sx={{ ...splitCellSx, textAlign: "center" }}>
											{totalSold > 0 ? splitSum : "—"}
										</TableCell>,
									];
								})}
							</TableRow>,
						];
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// ─── Browse Table (before report run) ───────────────────────────────────────
const BrowseTable = ({ rows = [], selectedOptionCode, onSelectOption }) => {
	if (!rows.length) {
		return (
			<Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1 }}>
				<Typography variant="body2" color="text.secondary">No options found for selected filters.</Typography>
			</Box>
		);
	}
	return (
		<TableContainer sx={{ border: "1px solid #d1d5db", borderRadius: 1 }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={headerCellSx}>#</TableCell>
						<TableCell sx={headerCellSx}>URL</TableCell>
						<TableCell sx={headerCellSx}>PKEY</TableCell>
						<TableCell sx={headerCellSx}>Season</TableCell>
						<TableCell sx={headerCellSx}>Category</TableCell>
						<TableCell sx={headerCellSx}>Sub Cat</TableCell>
						<TableCell sx={headerCellSx}>Color</TableCell>
						<TableCell sx={headerCellSx}>Palette</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, idx) => {
						const selected = selectedOptionCode === row.option_code;
						const displayPkey = buildDisplayPkey(row);
						return (
							<TableRow
								key={`browse-${row.option_code}-${idx}`}
								onClick={() => onSelectOption?.(selected ? null : row)}
								sx={{ cursor: "pointer", backgroundColor: selected ? "rgba(25,118,210,0.1)" : "transparent", "&:hover": { backgroundColor: "#f9fafb" } }}>
								<TableCell sx={bodyCellSx}>{idx + 1}</TableCell>
								<TableCell sx={bodyCellSx}>
									<ZoomableImage src={row.image_url} alt={displayPkey} />
								</TableCell>
								<TableCell sx={{ ...bodyCellSx, maxWidth: 200 }}>
									<Tooltip title={displayPkey} placement="top">
										<Typography sx={{ fontSize: "0.68rem", fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{displayPkey}</Typography>
									</Tooltip>
								</TableCell>
								<TableCell sx={bodyCellSx}>{row.season || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.category || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.sub_category || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.color || "-"}</TableCell>
								<TableCell sx={bodyCellSx}>{row.palette_type || "-"}</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// ─── Main Component ──────────────────────────────────────────────────────────
const SizeContribution = () => {
	const [options, setOptions] = useState([]);
	const [selectedOption, setSelectedOption] = useState(null);
	const [loadingOptions, setLoadingOptions] = useState(false);
	const [loadingReport, setLoadingReport] = useState(false);
	const [error, setError] = useState("");
	const [report, setReport] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [currentSeason, setCurrentSeason] = useState(getDefaultSeason());
	const [prevSeasonCount, setPrevSeasonCount] = useState(1);
	const [seasonOptions, setSeasonOptions] = useState([]);
	const [brandOptions, setBrandOptions] = useState([]);
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [brandFilter, setBrandFilter] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [optionPage, setOptionPage] = useState(1);
	const optionPageSize = 20;
	const [salesOptions, setSalesOptions] = useState({
		stores: [],
		states: [],
		regions: [],
	});
	const [salesFilters, setSalesFilters] = useState(() => ({ ...emptySalesFilterSelection }));

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			try {
				const rows = await getFilterOptions();
				if (!mounted) return;
				const seasons = (rows?.season || []).map((r) => r?.key).filter(Boolean);
				setBrandOptions((rows?.brand || []).map((r) => r?.key).filter(Boolean));
				setCategoryOptions((rows?.category || []).map((r) => r?.key).filter(Boolean));
				setSeasonOptions(seasons);
				if (seasons.length && !seasons.includes(currentSeason)) setCurrentSeason(seasons[0]);
			} catch (_) {}
		};
		load();
		return () => { mounted = false; };
	}, []);

	/** Dependent store / state / region lists (BigQuery), scoped to EBO/MBO/LFS and parent selections. */
	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const res = await getSalesFilterOptions({
					channel: salesFilters.channel,
					store: salesFilters.store,
					state: salesFilters.state,
				});
				if (!mounted) return;
				setSalesOptions({
					stores: res.stores || [],
					states: res.states || [],
					regions: res.regions || [],
				});
			} catch (err) {
				console.error("sales-filter-options", err?.response?.data || err);
				if (mounted) setSalesOptions({ stores: [], states: [], regions: [] });
			}
		})();
		return () => { mounted = false; };
	}, [salesFilters.channel, salesFilters.store, salesFilters.state]);

	useEffect(() => {
		let mounted = true;
		const timer = setTimeout(async () => {
			try {
				setLoadingOptions(true);
				const rows = await getOptionSearch({ brand: brandFilter, category: categoryFilter, season: currentSeason });
				if (!mounted) return;
				setOptions(rows);
				setOptionPage(1);
				setReport(null);
			} catch (err) {
				if (!mounted) return;
				setError(err?.response?.data?.message || "Unable to load options");
			} finally {
				if (mounted) setLoadingOptions(false);
			}
		}, 300);
		return () => { mounted = false; clearTimeout(timer); };
	}, [brandFilter, categoryFilter, currentSeason]);

	const runReport = async () => {
		if (!options.length) { setError("No options for selected filters"); return; }
		try {
			setLoadingReport(true);
			setError("");
			const payload = await getSizeContributionReport({
				optionCode: selectedOption?.option_code || "",
				optionCodes: selectedOption ? [selectedOption.option_code] : options.map((r) => r.option_code),
				currentSeason,
				prevSeasonCount: Number(prevSeasonCount),
				filterBrand: brandFilter,
				filterCategory: categoryFilter,
				salesFilters: buildSalesFiltersPayload(salesFilters),
			});
			setReport(payload);
		} catch (err) {
			setError(err?.response?.data?.message || "Unable to fetch report");
		} finally {
			setLoadingReport(false);
		}
	};

	const paginatedOptions = useMemo(() => options.slice((optionPage - 1) * optionPageSize, optionPage * optionPageSize), [options, optionPage]);
	const optionPageCount = Math.max(1, Math.ceil(options.length / optionPageSize));

	const prevSeasons = report?.prev_seasons || [];
	const currentOptions = report?.current_options || [];
	const activeAttrs = report?.config?.attributes || [];
	const reqFilters = report?.request_filters;
	const showBrand = reqFilters != null ? (reqFilters.filter_brand || "All") : (brandFilter || "All");
	const showCategory = reqFilters != null ? (reqFilters.filter_category || "All") : (categoryFilter || "All");
	const showSeason = reqFilters?.current_season || currentSeason;
	const showPrevN = reqFilters?.prev_season_count != null ? reqFilters.prev_season_count : prevSeasonCount;

	const salesScopeSummary = useMemo(() => {
		const sf = reqFilters?.sales_filters;
		if (!sf || typeof sf !== "object") return "";
		const parts = Object.entries(sf)
			.filter(([, v]) => v != null && String(v).trim() !== "")
			.map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
		return parts.join(" · ");
	}, [reqFilters]);

	return (
		<Box sx={{ p: 1 }}>
			{/* ST Pictorial–aligned sales drill-down (ALL_CHANNELS_SALES_DATA + PIN + SITE) — above Relook options */}
			<Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", mb: 1.5 }}>
				<Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#374151", mb: 1 }}>
					Historical sales scope (optional — EBO, MBO, LFS; refine by store, state, region)
				</Typography>
				<Stack direction="row" flexWrap="wrap" gap={1.5} useFlexGap>
					<Autocomplete
						multiple
						size="small"
						options={SALES_CHANNEL_OPTIONS}
						value={salesFilters.channel}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, channel: v, store: [], state: [], region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Channel"
								placeholder="All (EBO, MBO, LFS)"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.stores}
						value={salesFilters.store}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, store: v, state: [], region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Store"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.states}
						value={salesFilters.state}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, state: v, region: [] }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="State"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Autocomplete
						multiple
						size="small"
						options={salesOptions.regions}
						value={salesFilters.region}
						onChange={(_, v) => setSalesFilters((p) => ({ ...p, region: v }))}
						renderInput={(p) => (
							<TextField
								{...p}
								label="Region (zone)"
								placeholder="All"
								InputLabelProps={{ ...p.InputLabelProps, ...filterOutlinedLabelProps }}
							/>
						)}
						limitTags={1}
						sx={salesAutocompleteFieldSx}
					/>
					<Button size="small" variant="text" onClick={() => setSalesFilters({ ...emptySalesFilterSelection })} sx={{ alignSelf: "center" }}>
						Clear sales filters
					</Button>
				</Stack>
			</Paper>

			{/* Relook filter bar — brand / season / category / run (one grid: labels all float same as ST Pictorial) */}
			<Paper sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none", mb: 1.5 }}>
				<Stack direction={{ xs: "column", lg: "row" }} gap={1.5} alignItems={{ lg: "center" }} sx={{ width: "100%" }}>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, minmax(0, 1fr))" },
							gap: 1.5,
							flex: 1,
							minWidth: 0,
						}}>
						<TextField
							fullWidth
							size="small"
							select
							label="Brand Filter"
							value={brandFilter}
							onChange={(e) => setBrandFilter(e.target.value)}
							InputLabelProps={filterOutlinedLabelProps}
							SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
							sx={relookSelectFieldSx}>
							<MenuItem value="">All</MenuItem>
							{brandOptions.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
						</TextField>
						<TextField
							fullWidth
							size="small"
							select
							label="Current Season"
							value={currentSeason}
							onChange={(e) => setCurrentSeason(e.target.value)}
							InputLabelProps={filterOutlinedLabelProps}
							SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
							sx={relookSelectFieldSx}>
							<MenuItem value="">All</MenuItem>
							{seasonOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
						</TextField>
						<TextField
							fullWidth
							size="small"
							select
							label="Category Filter"
							value={categoryFilter}
							onChange={(e) => setCategoryFilter(e.target.value)}
							InputLabelProps={filterOutlinedLabelProps}
							SelectProps={{ displayEmpty: true, renderValue: (v) => (v == null || v === "" ? "All" : v) }}
							sx={relookSelectFieldSx}>
							<MenuItem value="">All</MenuItem>
							{categoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
						</TextField>
						<TextField
							fullWidth
							size="small"
							select
							label="Previous Seasons"
							value={prevSeasonCount}
							onChange={(e) => setPrevSeasonCount(Number(e.target.value))}
							InputLabelProps={filterOutlinedLabelProps}
							sx={relookSelectFieldSx}>
							{[1, 2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
						</TextField>
					</Box>
					<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0 }}>
						<Button
							variant="contained"
							onClick={runReport}
							disabled={loadingReport || loadingOptions || !options.length}
							sx={{ height: 40 }}>
							{loadingReport ? "Running..." : "Run Report"}
						</Button>
						{report && (
							<Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setReport(null)} sx={{ height: 40 }}>
								Clear
							</Button>
						)}
						<Tooltip title="Configure matching attributes">
							<IconButton onClick={() => setDrawerOpen(true)} size="small">
								<SettingsIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Paper>

			{/* Report summary strip */}
			{report && (
				<Paper sx={{ px: 2, py: 1, mb: 1.5, border: "1px solid #e5e7eb", boxShadow: "none", borderRadius: 2, background: "#f9fafb" }}>
					<Stack direction="row" gap={4} flexWrap="wrap" alignItems="flex-start">
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>BRAND FILTER</Typography><Typography sx={{ fontSize: "0.82rem" }}>{showBrand || "All"}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>CURRENT SEASON</Typography><Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{showSeason}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>CATEGORY FILTER</Typography><Typography sx={{ fontSize: "0.82rem" }}>{showCategory || "All"}</Typography></Box>
						<Box><Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>PREVIOUS SEASONS</Typography><Typography sx={{ fontSize: "0.82rem" }}>{prevSeasons.join(", ") || showPrevN}</Typography></Box>
						<Box>
							<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>PURPOSE</Typography>
							<Typography sx={{ fontSize: "0.8rem", color: "#059669", fontWeight: 600 }}>
								Size ratio suggestion for {showSeason} buy
							</Typography>
						</Box>
						{activeAttrs.length > 0 && (
							<Box sx={{ flex: 1, minWidth: 220 }}>
								<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>MATCHING ATTRIBUTES (from MongoDB)</Typography>
								<Typography sx={{ fontSize: "0.75rem" }}>{activeAttrs.map((a) => a.replace(/_/g, " ").toUpperCase()).join(", ")}</Typography>
							</Box>
						)}
						{salesScopeSummary ? (
							<Box sx={{ flex: 1, minWidth: 220 }}>
								<Typography sx={{ fontSize: "0.62rem", color: "#6b7280", fontWeight: 700 }}>HISTORICAL SALES SCOPE (BQ)</Typography>
								<Typography sx={{ fontSize: "0.72rem" }}>{salesScopeSummary}</Typography>
							</Box>
						) : null}
					</Stack>
				</Paper>
			)}

			{error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

			{loadingReport && <Stack alignItems="center" py={6}><CircularProgress /></Stack>}

			{/* Browse table — before report run */}
			{!report && !loadingReport && (
				<Box>
					<Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.8 }}>
						{currentSeason} Options ({options.length}){loadingOptions ? " — loading..." : ""}
						{selectedOption ? ` — Selected: ${buildDisplayPkey(selectedOption)}` : " — click a row to select, or Run Report on all"}
					</Typography>
					<BrowseTable rows={paginatedOptions} selectedOptionCode={selectedOption?.option_code} onSelectOption={setSelectedOption} />
					{options.length > optionPageSize && (
						<Stack alignItems="center" mt={1.5}>
							<Pagination color="primary" page={optionPage} count={optionPageCount} onChange={(_, v) => setOptionPage(v)} />
						</Stack>
					)}
				</Box>
			)}

			{/* Report table */}
			{!loadingReport && report && (
				<Box>
					<Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 0.8 }}>
						{currentSeason} Options ({currentOptions.length}) — with suggested size ratios from {prevSeasons.join(", ")}
					</Typography>
					<ReportTable currentOptions={currentOptions} prevSeasons={prevSeasons} currentSeason={currentSeason} />
				</Box>
			)}

			<AttrConfigDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				onSaved={() => {}}
			/>
		</Box>
	);
};

export default SizeContribution;
